import { CdkTextareaAutosize } from '@angular/cdk/text-field';
import { DatePipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  effect,
  ElementRef,
  inject,
  input,
  OnInit,
  output,
  signal,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { MatButton, MatIconButton } from '@angular/material/button';
import { MatFormField, MatSuffix } from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { MatInput } from '@angular/material/input';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { MatTooltip } from '@angular/material/tooltip';
import { switchMap } from 'rxjs';
import { WebSocketService } from '@ipg/ngx-frontend-toolset';
import { MarkdownPipe } from '../../pipes/markdown.pipe';
import { AiAgentChatLayoutService } from '../../services/ai-agent-chat-layout.service';
import { AiAgentSessionService } from '../../services/ai-agent-session.service';
import {
  AiAgentSession,
  AiAgentSessionWebSocketPayload,
  ChatInteractionPreview,
  ChatMessage,
  mapBackendSessionToUi,
} from './ai-agent-chat.models';

@Component({
  selector: 'app-ai-agent-chat',
  imports: [
    MatButton,
    MatIconButton,
    MatIcon,
    MatTooltip,
    MatFormField,
    MatInput,
    MatSuffix,
    MatProgressSpinner,
    CdkTextareaAutosize,
    FormsModule,
    DatePipe,
    MarkdownPipe,
  ],
  templateUrl: './ai-agent-chat.component.html',
  styleUrl: './ai-agent-chat.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AiAgentChatComponent implements OnInit {
  readonly clientId = input<string | null>(null);
  readonly isTemplate = input<boolean>(false);

  readonly closed = output<void>();
  readonly interactionCreated = output<string>();
  readonly previewChanged = output<ChatInteractionPreview | null>();

  protected readonly layoutService = inject(AiAgentChatLayoutService);
  private readonly _sessionService = inject(AiAgentSessionService);
  private readonly _wsService = inject(WebSocketService);
  private readonly _destroyRef = inject(DestroyRef);
  private readonly _messagesContainer =
    viewChild<ElementRef<HTMLDivElement>>('messagesContainer');

  protected readonly userInput = signal<string>('');
  protected readonly isGenerating = signal<boolean>(false);
  protected readonly currentActivity = signal<{
    message: string;
    icon?: string;
    activity?: string;
  } | null>(null);
  protected readonly isCreatingInteraction = signal<boolean>(false);
  protected readonly isHistoryOpen = signal<boolean>(false);
  protected readonly historySearch = signal<string>('');

  protected readonly sessions = signal<AiAgentSession[]>([]);
  protected readonly selectedSessionId = signal<string>('');
  protected readonly selectedPreviewMessageId = signal<string | null>(null);

  private readonly _knownInteractionIds = new Set<string>();

  // Tool chips that arrived for a session id this client does not hold yet.
  // The only session that can be in that state is one this user just created
  // and whose POST has not returned, so the buffer is keyed by session id and
  // is drained — or discarded — the moment that response lands.
  private readonly _pendingToolChips = new Map<string, ChatMessage[]>();
  private _awaitingCreatedSession = false;

  protected readonly currentSession = computed<AiAgentSession>(() => {
    const activeId = this.selectedSessionId();
    if (activeId) {
      const found = this.sessions().find((s) => s.id === activeId);
      if (found) {
        return found;
      }
    }

    return {
      id: '',
      title: 'Nieuwe interactie',
      agentName: 'interaction_creator',
      status: 'open',
      createdAt: new Date(),
      updatedAt: new Date(),
      messages: [],
      createdInteractionIds: [],
    };
  });

  protected readonly isCurrentSessionClosed = computed<boolean>(() => {
    return this.currentSession().status === 'closed';
  });

  protected readonly activePreview = computed<ChatInteractionPreview | null>(() => {
    const msgs = this.currentSession().messages;
    const selectedId = this.selectedPreviewMessageId();
    if (selectedId) {
      const selectedMsg = msgs.find((m) => m.id === selectedId);
      if (selectedMsg?.interactionPreview) {
        return selectedMsg.interactionPreview;
      }
    }
    const chosenMessageId = this.currentSession().selectedMessageId;
    if (chosenMessageId) {
      const chosenMsg = msgs.find((m) => m.id === chosenMessageId);
      if (chosenMsg?.interactionPreview) {
        return chosenMsg.interactionPreview;
      }
    }
    for (let i = msgs.length - 1; i >= 0; i--) {
      if (msgs[i].interactionPreview) {
        return msgs[i].interactionPreview ?? null;
      }
    }
    return null;
  });

  constructor() {
    effect(() => {
      const preview = this.activePreview();
      this.previewChanged.emit(preview);
    });
  }

  protected selectPreview(message: ChatMessage): void {
    if (!message.interactionPreview) {
      return;
    }
    this.selectedPreviewMessageId.set(message.id);
  }

  protected isPreviewActive(messageId: string): boolean {
    const selectedId = this.selectedPreviewMessageId();
    if (selectedId) {
      return selectedId === messageId;
    }
    const chosenMessageId = this.currentSession().selectedMessageId;
    if (chosenMessageId) {
      return chosenMessageId === messageId;
    }
    const msgs = this.currentSession().messages;
    for (let i = msgs.length - 1; i >= 0; i--) {
      if (msgs[i].interactionPreview) {
        return msgs[i].id === messageId;
      }
    }
    return false;
  }

  protected isChosenPreview(messageId: string): boolean {
    const session = this.currentSession();
    if (session.status !== 'closed') {
      return false;
    }
    if (session.selectedMessageId) {
      return session.selectedMessageId === messageId;
    }
    const msgs = session.messages;
    for (let i = msgs.length - 1; i >= 0; i--) {
      if (msgs[i].interactionPreview) {
        return msgs[i].id === messageId;
      }
    }
    return false;
  }

  protected readonly savedSessionsCount = computed<number>(() => {
    return this.sessions().filter(
      (s) => s.id && !s.id.startsWith('session-temp-')
    ).length;
  });

  protected readonly filteredSessions = computed<AiAgentSession[]>(() => {
    const q = this.historySearch().trim().toLowerCase();
    const list = this.sessions().filter(
      (s) => s.id && !s.id.startsWith('session-temp-')
    );

    if (!q) {
      return list;
    }
    return list.filter((s) => s.title.toLowerCase().includes(q));
  });

  ngOnInit(): void {
    this._setupWebSocket();
    this._loadSessions();
  }

  protected closeChat(): void {
    this.closed.emit();
  }

  protected toggleHistory(): void {
    this.isHistoryOpen.update((open) => !open);
  }

  protected selectSession(sessionId: string): void {
    this.selectedSessionId.set(sessionId);
    this.selectedPreviewMessageId.set(null);
    this.currentActivity.set(null);
    this.userInput.set('');

    const session = this.sessions().find((s) => s.id === sessionId);
    const lastMsg = session?.messages[session.messages.length - 1];
    if (
      session &&
      (session.status === 'open' || session.status === 'processing') &&
      lastMsg?.sender === 'user'
    ) {
      this.isGenerating.set(true);
      this.currentActivity.set({
        message: 'Aan het nadenken...',
        icon: 'psychology',
        activity: 'thinking',
      });
    } else {
      this.isGenerating.set(false);
    }

    this._scrollToBottom();
  }

  protected startNewSession(): void {
    this.isGenerating.set(false);
    this.currentActivity.set(null);
    this.userInput.set('');
    this.isHistoryOpen.set(false);
    this.selectedSessionId.set('');
    this.selectedPreviewMessageId.set(null);
    this._scrollToBottom();
  }

  protected startResize(event: PointerEvent): void {
    event.preventDefault();
    const target = event.currentTarget as HTMLElement;
    target.setPointerCapture(event.pointerId);
    this.layoutService.setDragging(true);

    const onPointerMove = (e: PointerEvent) => {
      const newWidth = window.innerWidth - e.clientX;
      this.layoutService.setWidth(newWidth);
    };

    const onPointerUp = (e: PointerEvent) => {
      try {
        target.releasePointerCapture(e.pointerId);
      } catch {
        // Pointer capture already released
      }
      this.layoutService.setDragging(false);
      target.removeEventListener('pointermove', onPointerMove as EventListener);
      target.removeEventListener('pointerup', onPointerUp as EventListener);
      target.removeEventListener('pointercancel', onPointerUp as EventListener);
    };

    target.addEventListener('pointermove', onPointerMove as EventListener);
    target.addEventListener('pointerup', onPointerUp as EventListener);
    target.addEventListener('pointercancel', onPointerUp as EventListener);
  }

  protected sendMessage(customPrompt?: string): void {
    if (this.isCurrentSessionClosed()) {
      return;
    }

    const textToSend = (customPrompt ?? this.userInput()).trim();
    if (!textToSend || this.isGenerating()) {
      return;
    }

    this.selectedPreviewMessageId.set(null);
    this.currentActivity.set({
      message: 'Aan het nadenken...',
      icon: 'psychology',
      activity: 'thinking',
    });

    const currentId = this.selectedSessionId();
    const userMessage: ChatMessage = {
      id: `user-${Date.now()}`,
      sender: 'user',
      text: textToSend,
      timestamp: new Date(),
    };

    if (!currentId) {
      // Lazy session creation: User sent first message in draft mode
      const tempId = `session-temp-${Date.now()}`;
      const optimisticSession: AiAgentSession = {
        id: tempId,
        title:
          textToSend.slice(0, 30) + (textToSend.length > 30 ? '...' : ''),
        agentName: 'interaction_creator',
        status: 'open',
        createdAt: new Date(),
        updatedAt: new Date(),
        messages: [userMessage],
        createdInteractionIds: [],
      };

      this.sessions.update((list) => [optimisticSession, ...list]);
      this.selectedSessionId.set(tempId);
      this.userInput.set('');
      this.isGenerating.set(true);
      this._awaitingCreatedSession = true;
      this._scrollToBottom();

      this._sessionService
        .create({
          clientId: this.clientId() ?? null,
          agentName: 'interaction_creator',
          title: 'Nieuwe interactie',
          initialMessage: textToSend,
        })
        .subscribe({
          next: (backendSession) => {
            const uiSession = mapBackendSessionToUi(backendSession);
            const merged = this._withLiveStatusMessages(
              uiSession,
              tempId,
              this._takeBufferedToolChips(uiSession.id)
            );
            this.sessions.update((list) => [
              merged,
              ...list.filter((s) => s.id !== tempId && s.id !== uiSession.id),
            ]);
            this.selectedSessionId.set(uiSession.id);
            this._checkForNewInteraction(merged);
          },
          error: () => {
            this._takeBufferedToolChips('');
            this.sessions.update((list) => list.filter((s) => s.id !== tempId));
            this.selectedSessionId.set('');
            this.isGenerating.set(false);
            this.currentActivity.set(null);
            this._scrollToBottom();
          },
        });
      return;
    }

    // Optimistic UI update for existing session
    this.sessions.update((list) =>
      list.map((s) => {
        if (s.id === currentId) {
          const updatedTitle =
            s.title === 'Nieuwe interactie'
              ? textToSend.slice(0, 30) + (textToSend.length > 30 ? '...' : '')
              : s.title;
          return {
            ...s,
            title: updatedTitle,
            status: 'open',
            updatedAt: new Date(),
            messages: [...s.messages, userMessage],
          };
        }
        return s;
      })
    );

    this.userInput.set('');
    this.isGenerating.set(true);
    this._scrollToBottom();

    // Call backend API to append message
    this._sessionService.sendMessage(currentId, { message: textToSend }).subscribe({
      next: (updatedSession) => {
        const merged = this._withLiveStatusMessages(
          mapBackendSessionToUi(updatedSession),
          currentId
        );
        this._updateSessionInList(merged);
        this._checkForNewInteraction(merged);
      },
      error: () => {
        this.isGenerating.set(false);
        this.currentActivity.set(null);
        this._scrollToBottom();
      },
    });
  }

  /**
   * Re-runs the turn a failure chip is offering to retry.
   *
   * Sends no message on purpose: the backend re-opens the session and the
   * agent replays the same transcript it had before the failure, so this is a
   * retry of the original request rather than a new turn asking for one.
   */
  protected retryFailedTurn(): void {
    const currentId = this.selectedSessionId();
    if (!currentId || this.isGenerating() || this.isCurrentSessionClosed()) {
      return;
    }

    this.currentActivity.set({
      message: 'Opnieuw proberen...',
      icon: 'refresh',
      activity: 'thinking',
    });
    this.isGenerating.set(true);
    this._scrollToBottom();

    this._sessionService.retry(currentId).subscribe({
      next: (updatedSession) => {
        const merged = this._withLiveStatusMessages(
          mapBackendSessionToUi(updatedSession),
          currentId
        );
        this._updateSessionInList(merged);
        this._checkForNewInteraction(merged);
      },
      error: () => {
        this.isGenerating.set(false);
        this.currentActivity.set(null);
        this._scrollToBottom();
      },
    });
  }

  protected createInteractionFromPreview(
    session: AiAgentSession,
    preview: ChatInteractionPreview,
    messageId?: string
  ): void {
    if (this.isCreatingInteraction() || !preview.rawJson) {
      return;
    }

    this.isCreatingInteraction.set(true);
    this._sessionService
      .createInteraction(session.id, preview.rawJson, messageId)
      .subscribe({
        next: (updatedBackendSession) => {
          const uiSession = this._withLiveStatusMessages(
            mapBackendSessionToUi(updatedBackendSession)
          );
          this._updateSessionInList(uiSession);
          this._checkForNewInteraction(uiSession);
          this.isCreatingInteraction.set(false);
          this.isGenerating.set(false);
          this.currentActivity.set(null);
          this._scrollToBottom();
        },
        error: (err) => {
          console.error('Failed to create interaction from session:', err);
          this.isCreatingInteraction.set(false);
          this._scrollToBottom();
        },
      });
  }

  protected completeSession(session: AiAgentSession): void {
    if (session.status === 'closed') {
      return;
    }

    this._sessionService
      .update(session.id, { status: 'closed' })
      .subscribe({
        next: (updated) => {
          const uiSession = this._withLiveStatusMessages(
            mapBackendSessionToUi(updated)
          );
          this._updateSessionInList(uiSession);
          this._checkForNewInteraction(uiSession);
          this.isGenerating.set(false);
          this.currentActivity.set(null);
          this._scrollToBottom();
        },
        error: () => {
          this.sessions.update((list) =>
            list.map((s) =>
              s.id === session.id
                ? { ...s, status: 'closed', updatedAt: new Date() }
                : s
            )
          );
          this.isGenerating.set(false);
          this.currentActivity.set(null);
          this._scrollToBottom();
        },
      });
  }

  protected handleKeyDown(event: KeyboardEvent): void {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      this.sendMessage();
    }
  }

  private _setupWebSocket(): void {
    this._wsService
      .connect('/event-stream', {
        requireAuth: true,
        topics: ['ai-agent-sessions/#', 'ai-agent-sessions'],
      })
      .pipe(
        switchMap((connection) => connection.events$),
        takeUntilDestroyed(this._destroyRef)
      )
      .subscribe({
        next: (event) => {
          const payload = event?.payload as
            | AiAgentSessionWebSocketPayload
            | undefined;
          const targetSessionId = payload?.sessionId || payload?.session_id;

          if (!targetSessionId) {
            return;
          }

          const eventType = event?.eventType || '';
          if (eventType === 'ai_agent_session.activity') {
            if (targetSessionId === this.selectedSessionId()) {
              this.isGenerating.set(true);
              this.currentActivity.set({
                message: payload?.message || 'Bezig met verwerken...',
                icon: payload?.icon || 'psychology',
                activity: payload?.activity || 'thinking',
              });
            }

            if (payload?.activity === 'tool_call') {
              const callId = payload?.callId || payload?.call_id;
              const toolMsgId =
                callId ||
                `tool-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
              const statusText =
                payload?.friendlyMessage ||
                payload?.friendly_message ||
                payload?.message ||
                'Informatie geraadpleegd';

              const newStatusMessage: ChatMessage = {
                id: toolMsgId,
                sender: 'status',
                text: statusText,
                icon: payload?.icon || 'smart_toy',
                timestamp: new Date(),
                severity: 'info',
              };

              // The topic is tenant-wide, so this chip only belongs to a
              // session we actually hold. The one exception is the session this
              // user just created, whose id we do not know until the POST
              // returns; buffer those and let the create handler drain them.
              if (this.sessions().some((s) => s.id === targetSessionId)) {
                this.sessions.update((list) =>
                  list.map((s) => {
                    if (
                      s.id !== targetSessionId ||
                      s.messages.some((m) => m.id === toolMsgId)
                    ) {
                      return s;
                    }
                    return {
                      ...s,
                      updatedAt: new Date(),
                      messages: [...s.messages, newStatusMessage],
                    };
                  })
                );
              } else if (this._awaitingCreatedSession) {
                this._bufferToolChip(targetSessionId, newStatusMessage);
              }
            }

            if (targetSessionId === this.selectedSessionId()) {
              this._scrollToBottom();
            }
            return;
          }

          // The event topic is tenant-wide, so most events concern sessions
          // owned by other users. Those are not ours to read — the backend
          // scopes reads to the owner and would 404 — and _updateSessionInList
          // only refreshes sessions already in the list, so fetching anything
          // we don't hold would be a wasted round-trip either way.
          const isKnown = this.sessions().some((s) => s.id === targetSessionId);
          if (!isKnown) {
            return;
          }

          // Fetch updated session data from backend
          this._sessionService.getById(targetSessionId).subscribe({
            next: (backendSession) => {
              const uiSession = this._withLiveStatusMessages(
                mapBackendSessionToUi(backendSession)
              );
              this._updateSessionInList(uiSession);
              this._checkForNewInteraction(uiSession);

              if (targetSessionId === this.selectedSessionId()) {
                if (
                  uiSession.status !== 'open' &&
                  uiSession.status !== 'processing'
                ) {
                  this.isGenerating.set(false);
                  this.currentActivity.set(null);
                }
                this._scrollToBottom();
              }
            },
          });
        },
        error: (err) => {
          console.error(
            'WebSocket event-stream connection failed for AI agent sessions:',
            err
          );
        },
      });
  }

  private _loadSessions(): void {
    this._sessionService
      .list({
        clientId: this.clientId() ?? null,
        agentName: 'interaction_creator',
      })
      .subscribe({
        next: (response) => {
          const backendSessions = response.items || [];
          const validSessions = backendSessions
            .map(mapBackendSessionToUi)
            .filter(
              (s) =>
                s.messages.length > 0 ||
                (s.createdInteractionIds && s.createdInteractionIds.length > 0)
            );

          for (const s of validSessions) {
            for (const id of s.createdInteractionIds ?? []) {
              this._knownInteractionIds.add(id);
            }
          }

          this.sessions.set(validSessions);
          this.selectedSessionId.set('');
          this.isGenerating.set(false);
        },
        error: () => {
          this.sessions.set([]);
          this.selectedSessionId.set('');
          this.isGenerating.set(false);
        },
      });
  }

  private _checkForNewInteraction(uiSession: AiAgentSession): void {
    for (const id of uiSession.createdInteractionIds ?? []) {
      if (!this._knownInteractionIds.has(id)) {
        this._knownInteractionIds.add(id);
        this.interactionCreated.emit(id);
      }
    }
  }

  private _updateSessionInList(updatedSession: AiAgentSession): void {
    this.sessions.update((list) =>
      list.map((s) => (s.id === updatedSession.id ? updatedSession : s))
    );
  }

  /**
   * A session read back from the backend only carries the status chips that
   * were persisted at the time of the read. Chips the websocket delivered
   * since then live only in the copy we hold, so carry them over rather than
   * letting the refresh drop them mid-run. Chip ids are stable per tool call,
   * so a chip the response already carries is never added twice.
   *
   * ``previousId`` is where to look for the copy we hold — it differs from the
   * refreshed session's own id only while a freshly created session is still
   * filed under its temporary id.
   */
  private _withLiveStatusMessages(
    uiSession: AiAgentSession,
    previousId: string = uiSession.id,
    bufferedChips: ChatMessage[] = []
  ): AiAgentSession {
    const previous = this.sessions().find((s) => s.id === previousId);
    const carried = [
      ...(previous?.messages || []).filter((m) => m.sender === 'status'),
      ...bufferedChips,
    ];
    const missing = carried.filter(
      (chip, index) =>
        !uiSession.messages.some((m) => m.id === chip.id) &&
        carried.findIndex((other) => other.id === chip.id) === index
    );

    if (missing.length === 0) {
      return uiSession;
    }

    return {
      ...uiSession,
      messages: [...uiSession.messages, ...missing],
    };
  }

  private _bufferToolChip(sessionId: string, chip: ChatMessage): void {
    const buffered = this._pendingToolChips.get(sessionId) || [];
    if (buffered.some((m) => m.id === chip.id)) {
      return;
    }
    this._pendingToolChips.set(sessionId, [...buffered, chip]);
  }

  /**
   * Hand back the chips buffered for ``sessionId`` and drop the buffer. The
   * create request has resolved by now, so anything still buffered belongs to
   * another user's session and is not ours to show.
   */
  private _takeBufferedToolChips(sessionId: string): ChatMessage[] {
    const chips = this._pendingToolChips.get(sessionId) || [];
    this._pendingToolChips.clear();
    this._awaitingCreatedSession = false;
    return chips;
  }

  private _scrollToBottom(): void {
    setTimeout(() => {
      const container = this._messagesContainer()?.nativeElement;
      if (container) {
        container.scrollTop = container.scrollHeight;
      }
    }, 50);
  }
}
